import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw, X } from 'lucide-react';
import { getServedAppVersion } from '../services/api';
import {
  getServedVersion,
  readBuiltVersion,
  shouldShowVersionBanner,
  subscribeServedVersion,
} from '../utils/appVersion';

/** Sondage de la version servie (un élève qui écrit du code ne fait aucun autre appel pendant des minutes). */
const POLL_INTERVAL_MS = 60_000;

interface NewVersionNoticeProps {
  onReload: () => void;
  onDismiss: () => void;
}

/** Texte et boutons du bandeau, sans logique : testable seul. */
export const NewVersionNotice: React.FC<NewVersionNoticeProps> = ({ onReload, onDismiss }) => (
  <div
    role="status"
    data-testid="new-version-banner"
    className="fixed bottom-4 left-4 right-4 z-40 max-w-sm rounded-lg border border-blue-200 bg-white p-3 text-sm text-gray-800 shadow-lg dark:border-blue-800 dark:bg-slate-900 dark:text-gray-200"
  >
    <div className="flex items-start gap-2">
      <RefreshCw className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">Nouvelle version disponible</p>
        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
          Recharger efface le code que vous n'avez pas encore soumis : finissez d'abord ce que vous écrivez.
        </p>
        <button
          type="button"
          onClick={onReload}
          className="mt-2 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
        >
          Recharger
        </button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fermer l'annonce de nouvelle version"
        className="rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-slate-800"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  </div>
);

/**
 * Annonce qu'une nouvelle version de la plateforme est en ligne. Jamais de rechargement forcé :
 * l'élève décide. Fermable (revient seulement pour une version encore plus récente), et muet sur
 * les pages de l'examen blanc et de l'Entraînement IA.
 */
const NewVersionBanner: React.FC = () => {
  const location = useLocation();
  const served = useSyncExternalStore(subscribeServedVersion, getServedVersion, () => null);
  const [built] = useState(() => readBuiltVersion());
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);

  useEffect(() => {
    // Pas de balise de version (développement) : rien à comparer, aucun sondage.
    if (!built) return;
    const poll = () => {
      if (document.visibilityState === 'hidden') return;
      // L'en-tête de la réponse met la version à jour (client.ts) ; un échec réseau est sans conséquence.
      getServedAppVersion().catch(() => undefined);
    };
    poll();
    const timer = window.setInterval(poll, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', poll);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [built]);

  const reload = useCallback(() => window.location.reload(), []);
  const dismiss = useCallback(() => setDismissedFor(served), [served]);

  if (!shouldShowVersionBanner({ built, served, dismissedFor, pathname: location.pathname })) return null;
  return <NewVersionNotice onReload={reload} onDismiss={dismiss} />;
};

export default NewVersionBanner;
